import { Link } from 'react-router-dom'
import { formatPrice, pricePerHour, type Locale, type Offer, type OfferKind } from '@diana/shared'
import { useContent } from '../hooks/useContent.js'
import type { ContactSettings } from '../lib/api.js'
import { useLocale, usePath } from '../i18n/index.js'

type Strings = {
  featured: string
  free: string
  min: string
  perHour: string
  /** Button text when Diana hasn't written her own. */
  cta: Record<OfferKind, string>
}

const T: Record<Locale, Strings> = {
  pl: {
    featured: 'Najczęściej wybierane',
    free: 'Gratis',
    min: 'min',
    perHour: '/h',
    cta: { trial: 'Umów rozmowę', single: 'Umów lekcję', package: 'Zapisz się', contact: 'Skontaktuj się' },
  },
  uk: {
    featured: 'Найчастіше обирають',
    free: 'Безкоштовно',
    min: 'хв',
    perHour: '/год',
    cta: {
      trial: 'Записатися на розмову',
      single: 'Записатися на урок',
      package: 'Записатися',
      contact: 'Написати мені',
    },
  },
  ru: {
    featured: 'Выбирают чаще всего',
    free: 'Бесплатно',
    min: 'мин',
    perHour: '/ч',
    cta: {
      trial: 'Записаться на встречу',
      single: 'Записаться на урок',
      package: 'Записаться',
      contact: 'Написать мне',
    },
  },
}

/**
 * One position from the cennik.
 *
 * What the button does depends on the kind: bookable offers open the
 * calendar with the offer pre-selected; a `contact` offer (group lessons,
 * the speaking club) goes to Diana's own link, then the Telegram link from
 * settings, then the contact page — whichever exists first.
 */
export function OfferCard({ offer }: { offer: Offer }) {
  const locale = useLocale()
  const s = T[locale]
  const to = usePath()
  // Same query the page already made — served from the cache, no extra request.
  const { data } = useContent(locale)
  const telegram = ((data?.settings.contact as ContactSettings | undefined)?.telegram ?? '').trim()

  const isFree = offer.priceGrosz === 0
  const perHour = pricePerHour(offer.priceGrosz, offer.durationMin)
  const showPerHour = !isFree && offer.durationMin > 0 && offer.durationMin !== 60
  const ctaLabel = offer.ctaLabel || s.cta[offer.kind]

  return (
    <article className={`relative flex flex-col p-7 ${offer.featured ? 'card-feature' : 'card'}`}>
      {offer.coverUrl && (
        <div className="-mx-7 -mt-7 mb-6 aspect-[16/10] overflow-hidden rounded-t-[0.25rem] border-b border-sky-200">
          <img src={offer.coverUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
        </div>
      )}

      {offer.featured && (
        <span className="absolute -top-3 left-7 bg-gold px-3 py-1 font-roboto text-[0.6rem] font-bold uppercase tracking-[0.18em] text-porcelain">
          {s.featured}
        </span>
      )}

      <h3 className="h-condensed mt-1 text-base text-ink">{offer.title}</h3>
      {offer.subtitle && <p className="h-display mt-1.5 text-sm italic text-gold-600">{offer.subtitle}</p>}

      <div className="mt-6">
        {offer.priceLabel ? (
          <span className="h-display block text-2xl leading-tight text-gold">{offer.priceLabel}</span>
        ) : (
          <>
            {/* Gold on the numeral only — the one place it always looks expensive. */}
            <span className="h-display text-[2.5rem] leading-none text-gold">
              {isFree ? s.free : formatPrice(offer.priceGrosz)}
            </span>
            {offer.priceNote && (
              <span className="ml-2 font-roboto text-[0.65rem] uppercase tracking-[0.16em] text-ink-faint">
                {offer.priceNote}
              </span>
            )}
          </>
        )}
      </div>

      {offer.durationMin > 0 && (
        <p className="mt-2 font-roboto text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint">
          {offer.durationMin} {s.min}
          {showPerHour && ` · ${formatPrice(perHour)}${s.perHour}`}
        </p>
      )}

      <div className="rule-gold mt-6 w-full" />

      {offer.description && (
        <p className="mt-5 text-[0.9rem] leading-relaxed text-ink-soft">{offer.description}</p>
      )}

      {offer.includes.length > 0 && (
        <ul className="mt-5 space-y-2.5">
          {offer.includes.map((item) => (
            <li key={item} className="flex gap-3 text-[0.85rem] leading-relaxed font-light">
              <span aria-hidden className="mt-2.5 h-px w-3 shrink-0 bg-gold-300" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto pt-8">
        <OfferButton offer={offer} label={ctaLabel} telegram={telegram} contactPath={to('/kontakt')} bookingPath={to('/rezerwacja')} />
      </div>
    </article>
  )
}

function OfferButton({
  offer,
  label,
  telegram,
  contactPath,
  bookingPath,
}: {
  offer: Offer
  label: string
  telegram: string
  contactPath: string
  bookingPath: string
}) {
  const className = 'btn btn-outline btn-sm w-full'

  if (offer.kind !== 'contact') {
    return (
      <Link to={`${bookingPath}?oferta=${encodeURIComponent(offer.slug)}`} className={className}>
        {label}
      </Link>
    )
  }

  const href = offer.ctaUrl || telegram || contactPath
  if (/^https?:\/\//.test(href)) {
    return (
      <a href={href} target="_blank" rel="noreferrer noopener" className={className}>
        {label}
      </a>
    )
  }
  return (
    <Link to={href} className={className}>
      {label}
    </Link>
  )
}
